import React, { useState, useCallback, useEffect } from "react";
import Joyride, { CallBackProps, STATUS, Step, EVENTS } from "react-joyride";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import AgentAvatar from "./AgentAvatar";
import AgentChat from "./AgentChat";
import { agentConfig, Feature } from "./agentConfig";

const ProjectAgent: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [run, setRun] = useState(false);
    const [steps, setSteps] = useState<Step[]>([]);
    const [stepIndex, setStepIndex] = useState(0);

    // Check for first-time user
    useEffect(() => {
        const hasSeenWelcome = localStorage.getItem("hasSeenAgentWelcome");
        if (!hasSeenWelcome) {
            // Small delay to let the app load
            const timer = setTimeout(() => {
                setIsOpen(true);
                localStorage.setItem("hasSeenAgentWelcome", "true");
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, []);

    // Helper to handle navigation before starting a tour
    const handleStartTour = useCallback((feature?: Feature) => {
        setIsOpen(false);

        // Add disableBeacon to all steps for smoother experience
        const processSteps = (stepsToProcess: Step[]) =>
            stepsToProcess.map(s => ({ ...s, disableBeacon: true }));

        if (feature) {
            // Guide Mode: specific feature
            if (feature.route && location.pathname !== feature.route) {
                navigate(feature.route);
                // Delay starting the tour slightly to allow navigation to complete
                setTimeout(() => {
                    setSteps(processSteps(feature.steps));
                    setStepIndex(0);
                    setRun(true);
                }, 500);
            } else {
                setSteps(processSteps(feature.steps));
                setStepIndex(0);
                setRun(true);
            }
        } else {
            // Presentation Mode: all features concatenated
            // This is trickier with navigation. We might need a custom flow.
            // For simplicity, let's just start with the first feature's route if it exists
            // and maybe chain them? 
            // A simple concatenation won't work if they are on different pages.
            // We'll implement a "Playlist" approach.

            const playlist = agentConfig.features;
            startPlaylist(playlist);
        }
    }, [location.pathname, navigate]);

    const startPlaylist = (features: Feature[]) => {
        // This is a simplified version. Real multi-page tours are complex.
        // We will just concatenate all steps, but this won't handle navigation between steps automatically
        // unless we add logic to the callback.

        // Better approach for Presentation Mode:
        // 1. Start with the first feature.
        // 2. When it ends, check if there's a next feature.
        // 3. Navigate to next feature's route.
        // 4. Start next feature's steps.

        // For now, let's just do the first feature as a demo of "Presentation Mode" 
        // or maybe just the Dashboard one if they are not on a specific page.
        // Or we can try to chain them if we use a custom state for "currentFeatureIndex".

        // Let's implement a simple "Chain" manager
        // We'll store the list of features to run
        setFeatureQueue(features);
        processNextFeature(features, 0);
    };

    const [featureQueue, setFeatureQueue] = useState<Feature[]>([]);
    const [currentFeatureIndex, setCurrentFeatureIndex] = useState(-1);

    const processNextFeature = (queue: Feature[], index: number) => {
        if (index >= queue.length) {
            setRun(false);
            setCurrentFeatureIndex(-1);
            return;
        }

        const feature = queue[index];
        setCurrentFeatureIndex(index);

        const processSteps = (stepsToProcess: Step[]) =>
            stepsToProcess.map(s => ({ ...s, disableBeacon: true }));

        if (feature.route && location.pathname !== feature.route) {
            navigate(feature.route);
            setTimeout(() => {
                setSteps(processSteps(feature.steps));
                setStepIndex(0);
                setRun(true);
            }, 1500);
        } else {
            // Even if no route change, give a small buffer if previous step had an action
            setTimeout(() => {
                setSteps(processSteps(feature.steps));
                setStepIndex(0);
                setRun(true);
            }, 1000);
        }
    };

    const handleJoyrideCallback = (data: CallBackProps) => {
        const { action, index, status, type } = data;
        const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

        if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
            // Update step index for controlled mode
            // This is critical for the tour to advance
            const nextIndex = index + (action === 'prev' ? -1 : 1);
            setStepIndex(nextIndex);
        }

        if (finishedStatuses.includes(status)) {
            setRun(false);

            // If we are in a playlist (Presentation Mode), go to next feature
            if (currentFeatureIndex !== -1 && currentFeatureIndex < featureQueue.length - 1) {
                if (status === STATUS.FINISHED) {
                    setTimeout(() => {
                        processNextFeature(featureQueue, currentFeatureIndex + 1);
                    }, 500);
                } else {
                    setCurrentFeatureIndex(-1);
                    setFeatureQueue([]);
                }
            } else {
                setCurrentFeatureIndex(-1);
                setFeatureQueue([]);
            }
        }
    };

    // Effect to handle actions and voice when step changes
    useEffect(() => {
        if (run && steps[stepIndex]) {
            const currentStep = steps[stepIndex] as any;

            // 0. Speak the content
            if (currentStep.content) {
                // Strip HTML tags if any (basic check)
                const text = typeof currentStep.content === 'string'
                    ? currentStep.content
                    : (currentStep.content.props?.children || "");

                if (typeof text === 'string') {
                    import('../../utils/voiceUtils').then(({ speak }) => {
                        speak(text);
                    });
                }
            }

            // 1. Execute Action if present
            if (currentStep.data?.action) {
                const { selector, type, value } = currentStep.data.action;

                setTimeout(() => {
                    if (type === 'click') {
                        import('../../utils/simulationUtils').then(({ simulateClick }) => {
                            simulateClick(selector);
                        });
                    } else if (type === 'type' && value) {
                        import('../../utils/simulationUtils').then(({ simulateType }) => {
                            simulateType(selector, value);
                        });
                    }
                }, 500);
            }

            // 2. Auto-advance for "Presentation Mode" (if it's a playlist)
            // We can infer Presentation Mode if featureQueue is not empty
            if (featureQueue.length > 0) {
                // Calculate delay based on text length (approx reading speed) + action time
                const text = typeof currentStep.content === 'string' ? currentStep.content : "";
                // Increased to 100ms per char (slower reading) and min 5s base
                const readingTime = Math.max(5000, text.length * 100);
                const actionTime = currentStep.data?.action ? 2000 : 0; // Extra buffer for actions
                const autoAdvanceDelay = readingTime + actionTime;

                const timer = setTimeout(() => {
                    // Only auto-advance if we haven't finished the steps
                    if (stepIndex < steps.length - 1) {
                        setStepIndex(prev => prev + 1);
                    }
                }, autoAdvanceDelay);

                return () => clearTimeout(timer);
            }
        }

        // Cleanup speech when unmounting or changing steps (though speak() handles cancel too)
        return () => {
            import('../../utils/voiceUtils').then(({ cancelSpeech }) => {
                // We don't necessarily want to cancel immediately on step change as it might sound choppy
                // But we definitely want to cancel if we stop running
            });
        };
    }, [stepIndex, steps, run, featureQueue.length]);

    // Cleanup on unmount or when run stops
    useEffect(() => {
        if (!run) {
            import('../../utils/voiceUtils').then(({ cancelSpeech }) => {
                cancelSpeech();
            });
        }
    }, [run]);

    return (
        <>
            <Joyride
                steps={steps}
                run={run}
                stepIndex={stepIndex}
                continuous
                showProgress
                showSkipButton
                disableOverlayClose={true} // Prevent accidental closing
                hideCloseButton={featureQueue.length > 0} // Hide close button in auto mode
                callback={handleJoyrideCallback}
                styles={{
                    options: {
                        primaryColor: theme.palette.primary.main,
                        zIndex: 10000,
                    },
                }}
            />

            <AgentChat
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                config={agentConfig}
                onStartTour={handleStartTour}
            />

            <AgentAvatar
                isOpen={isOpen}
                onClick={() => setIsOpen(!isOpen)}
            />
        </>
    );
};

export default ProjectAgent;
